import { computed, effect, signal } from "@preact/signals";

export interface FrameItem {
    type: 'frame',
    no: string;
    name: string;
    thumbnail: string;
    images: string[];
    attributes: {
        [key: string]: string|string[]
    }
}

export interface CategoryItem {
    type: 'category',
    key: string;
}

export interface FrameListState { 
    list: {
        key: string;
        items: FrameItem[];
    }[];
    facets: {[key: string]: {title: string; buckets: {key: string; doc_count: number; id: string;}[]}},
    facetOrder: string[],
    attributesTitleMap: {[key: string]: string},
    categoryTitleMap: {[key: string]: string},
}

export interface FrameListFilterState {
    [key: string]: Set<string>;
}



export async function getFrames(filter: {
    [key: string]: Set<string>,
}) {
    const apiUrl = 'api.php?func=frames';
    const response = await fetch(apiUrl, {method: 'POST', 
        headers: { 'content-type': 'application/json'},
        body: JSON.stringify({ filter },  (_key, value) => (value instanceof Set ? [...value] : value)) });
    const result = await response.json();
    return result;
}

export async function getFrameByNo(no: string) {
    const apiUrl = 'api.php?func=frame_by_no&no=' + no;
    const response = await fetch(apiUrl, {method: 'GET'});
    const result = await response.json();
    return result;
}

const framesSignal = signal<FrameListState>({
    list: [],
    facets: {},
    facetOrder: [],
    attributesTitleMap: {},
    categoryTitleMap: {},
});

const flatFramesList = computed(() => {
    return framesSignal.value?.list.reduce((flatList: (FrameItem)[], category: any) => {
        flatList.push(...category.items);
        return flatList;
    }, []) ?? [];
});


const params = new URLSearchParams(location.search);
const initialFilter: {[key: string]: Set<string>} = {};
['gender', 'material', 'property', 'shape'].forEach(key => {
    const values = params.getAll(key + '[]');
    if (values.length === 0) {
        return;
    }
    initialFilter[key] = new Set(values);
})
const filterSignal = signal<FrameListFilterState>(initialFilter);

const filterQuery = computed(() => {
    const params = new URLSearchParams();
    Object.keys(filterSignal.value).forEach(key => {
        Array.from(filterSignal.value[key]).forEach(value => {
            params.append(key + '[]', value);
        });
    });

    const query = params.toString();

    return query ? '?' + query : '';
});

const selectedFrameNo = signal<string>('');

const selectedIndex = signal(0);

let loadedReservedFrameList: FrameItem[] = [];
let knowHowToSwipeFlagLoad = false;
try {
    const jsonList = localStorage.getItem('reserved-frame-list');
    if (jsonList) {
        const list = JSON.parse(jsonList);
        loadedReservedFrameList = Array.isArray(list) ? list : [];
    }

    const flag = localStorage.getItem('know-how-to-swipe');
    if (flag === 'true') {
        knowHowToSwipeFlagLoad = true;
    }
} catch(e) {

}


const reservedFrameList = signal<FrameItem[]>(loadedReservedFrameList);

const reservedDialogOpen = signal<boolean>(false);

const knowHowToSwipeFlag = signal<boolean>(knowHowToSwipeFlagLoad);



effect(() => {
    const query = filterQuery.value;

    if (query === location.search) {
        return;
    }
    
    window.history.pushState({ filter: filterSignal.value }, 
        ['Brillen von durchblick', 'Ihr fairer Optiker'].join(' - '), 
         window.location.pathname + query);
});

effect(() => {
    localStorage.setItem('reserved-frame-list', JSON.stringify(reservedFrameList.value));
});

effect(() => {
    localStorage.setItem('know-how-to-swipe', JSON.stringify(knowHowToSwipeFlag.value));
});




export const framesStore = {
    load() {
        getFrames(filterSignal.value).then(result => {
            framesSignal.value = result;
        });
    },



    updateFilter(filter: {
        [key: string]: Set<string>,
    }) {
        filterSignal.value = filter;

        this.load();
    },

    knowHowToSwipe() {
        knowHowToSwipeFlag.value = true;
    },

    selectFrame(no: string) {
        if (!no) {
            return;
        }
        selectedFrameNo.value = no;

        const found = flatFramesList.value.findIndex(frame => frame.no === '' + selectedFrameNo.value);
        if (found >= 0) {
            selectedIndex.value = found;
        } else {
            getFrameByNo(selectedFrameNo.value).then(frame => {
                framesSignal.value = frame;
                selectedIndex.value = 0;
            });
        }
    },

    selectIndex(index: number) {
        selectedIndex.value = index;
    },


    addFrameToReservedList(frame: FrameItem) {
        if (reservedFrameList.value.find(f => f.no === frame.no)) {
            return;
        }
        reservedFrameList.value = [...reservedFrameList.value, frame];
    },

    removeFrameFromReservedList(frame: FrameItem) {
        const existsIndex = reservedFrameList.value.findIndex(f => f.no === frame.no);
        if (existsIndex >= 0) {
            const list = reservedFrameList.value.slice(0);
            list.splice(existsIndex, 1);
            reservedFrameList.value = list;
        }
    },

    filter: filterSignal,

    filterQuery,

    facetOrder: computed(() => framesSignal.value?.facetOrder ?? []),

    facets: computed(() => framesSignal.value.facets),

    flatFrameAndCategoryList: computed(() => {
        return framesSignal.value?.list.reduce((flatList: (FrameItem|CategoryItem)[], category: any, index: number) => {
            if (index !== 0) {
                flatList.push({
                    key: category.key,
                    type: 'category',
                });
            }
            flatList.push(...category.items);
            return flatList;
        }, []) ?? [];
    }),

    flatFramesList,

    selectedIndex,

    selectedFrame: computed(() => {
        return flatFramesList.value[selectedIndex.value];
    }),

    attributesTitleMap: computed(() => framesSignal.value.attributesTitleMap),
    categoryTitleMap: computed(() => framesSignal.value.categoryTitleMap),

    reservedFrameList,

    reservedDialogOpen,

    dateSubject: signal<string>(''),

    knowHowToSwipeFlag,

}
